/** Strings of the project settings page (`/projects/:slug/settings`): `projSetEn` is the source, `projSetCs` must have the same keys. */
export const projSetEn = {
  projSet: {
    common: {
      saving: "Saving…",
      saveFailed: "Save failed.",
      tryAgain: "Try again.",
      couldntSave: "Couldn't save",
      error: "Error",
      add: "Add",
      remove: "Remove",
      name: "Name",
      save: "Save",
      cancel: "Cancel",
      productionMaintainerRequired:
        "Pick the production maintainer in Team first: every environment after the first is merged by a person.",
    },
    page: {
      noAccess: "Only a project manager or a Hub administrator can edit this project.",
      title: "Edit Project",
    },
    danger: {
      deleteFailed: "Delete failed.",
      couldntDelete: "Couldn't delete project",
      title: "Danger Zone",
      description:
        "Permanently delete this project and all associated data. This cannot be undone.",
      confirmLabel: "Type the project name to confirm:",
      deleting: "Deleting…",
      delete: "Delete Project",
    },
    sla: {
      saved: "SLA saved",
      couldntSave: "Couldn't save SLA",
      title: "Incident SLA",
      description:
        "Maximum response time per priority. Used to auto-compute due dates for new incidents.",
      urgent: "Urgent",
      high: "High",
      normal: "Normal",
      low: "Low",
      save: "Save SLA",
      hours: "hours",
      businessDays: "business days",
    },
    tester: {
      failed: "The check couldn't be run.",
      title: "Prokop's access",
      description:
        "What Prokop finds when he opens this project: the testing browser, his mailbox for sign-ups, the account Vault holds for the site, and whether that account actually signs him in. Nothing is written anywhere — he just tries.",
      checking: "Checking…",
      check: "Check {{env}}",
      noEnv: "No environment is enabled for testing (production is never signed into).",
    },
    external: {
      saved: "External services saved",
      couldntSave: "Couldn't save the external services",
      title: "External services (Prokop)",
      description:
        "Systems Prokop may open in the browser when a ticket's effect shows up outside the tested site — the customer's ERP, a payment gateway's admin, an invoicing system. He finds the account for each one in Vault by its URL and then interacts freely, so <strong>only ever store test credentials there</strong>. Anything not listed here is unreachable for him.",
      notes: "What it's for",
      folderUid: "Grafana folder uid",
      folderUidHint:
        "The uid of the project's folder in Grafana (the segment right after /dashboards/f/ in the folder's URL, e.g. grafana.netlion.cz/dashboards/f/afp5ev4kc24n4c/hravesporty gives afp5ev4kc24n4c, not the trailing name). When set, the project's alert rules, alerts and dashboards are read only from that folder, even if its name differs. Without it Hub looks for a folder whose name matches the project's (HraveSporty.cz finds HraveSporty); only a project with neither a uid nor such a folder is told that no folder is assigned. A folder with no rules shows as empty, which is not the same as not configured.",
      add: "Add service",
      save: "Save external services",
    },
    freelo: {
      title: "Freelo",
      description:
        "Two-way sync of the project's tickets with a Freelo todo list. Hours are logged in Freelo under the user whose e-mail and API key you enter here. The key is stored encrypted and never shown again. Sync runs every 5 minutes; if Freelo is down or rejects the key, it stops and the last error shows here.",
      projectId: "Freelo project id",
      todolistId: "Todo list id",
      email: "E-mail of the Freelo user",
      apiKey: "API key",
      apiKeySet: "Set (leave empty to keep it)",
      apiKeyMissing: "Not set",
      enabled: "Sync enabled",
      lastError: "Last error",
      lastSuccess: "Last successful run",
      never: "never",
      saved: "Freelo saved",
      couldntSave: "Couldn't save the Freelo connection",
      save: "Save Freelo",
    },
    general: {
      colour: "Colour",
      colourLabel: "Project colour",
      colourHexLabel: "Project colour hex",
      automatic: "Automatic",
      colourHint:
        "Shown next to this project's name across Hub. Automatic derives it from the project — currently {{colour}}.",
      colours: {
        blue: "Blue",
        orange: "Orange",
        aqua: "Aqua",
        yellow: "Yellow",
        magenta: "Magenta",
        green: "Green",
        violet: "Violet",
        red: "Red",
      },
      saved: "Project saved",
      saveFailed: "Save failed — check the fields and your permissions.",
      title: "General",
      description: "Basic project information",
      slug: "Slug",
      slugHint: "URL slug for this project.",
      customer: "Customer",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Project description",
      mirroredFrom: "Mirrored from",
      mirroredHint:
        "The upstream the GitLab repo mirrors two-way. With a GitHub URL here, Hub's merge gates refuse to merge while GitHub is ahead of GitLab (see wiki → Synchronizace).",
      label: "Label",
      public: "Public project",
      save: "Save Changes",
    },
    workflow: {
      saved: "Workflow saved",
      couldntSave: "Couldn't save workflow",
      title: "Workflow",
      description:
        "Turn Hub features on or off for this project and tune the development lifecycle.",
      features: "Features",
      releases: "Releases",
      releasesDesc:
        "Group work items into manually managed releases (mirrored as GitLab milestones).",
      sla: "Incident SLA",
      slaDesc: "Derive incident due dates from the project's SLA targets.",
      spec: "Technical specification",
      specDesc:
        "Adds a spec drafting + client sign-off phase between New and estimation. The manager owns it.",
      fallback: "Agents only when idle",
      fallbackDesc:
        "Metoděj and Prokop take this project's tickets only when they have nothing on other projects.",
      priority: "Project priority",
      priorityDesc:
        "Agents complete projects with higher priority before starting new tickets of less important ones.",
      priority_critical: "Critical",
      priority_high: "High",
      priority_normal: "Normal",
      priority_low: "Low",
      lifecycle: "Lifecycle steps",
      lifecycleHint:
        "Optional steps in the feature workflow. Which testing steps exist is configured per environment on the Environments card.",
      estimation: "Estimation",
      estimationDesc: "Developer estimates the work and a manager approves before it starts.",
      save: "Save Workflow",
    },
    env: {
      saved: "Environments saved",
      couldntSave: "Couldn't save the environments",
      title: "Environments",
      description:
        "Turn each environment on or off, set its URL, and declare which testing steps happen on it — each checked step adds one state to the feature workflow (internal testing always precedes client testing).",
      enabledLabel: "{{env}} enabled",
      internalLabel: "{{env}} internal testing",
      clientLabel: "{{env}} client testing",
      weTest: "We test here",
      clientTests: "Client tests here",
      personOnly:
        'On production only a person tests: turn on "A person tests before production" in Team to test here yourselves.',
      save: "Save environments",
      restarting: "Restarting {{name}}",
      pipelineLink: "Pipeline in GitLab",
      restartFailed: "Couldn't restart {{name}}",
      restartHint: "Runs a new pipeline on the branch, which deploys the stack again",
      startingPipeline: "Starting pipeline…",
      restart: "Restart {{name}}",
      idleNote:
        "A dev or test environment with no new pipeline for 20 days is stopped on the server. Restart brings it back with its data.",
    },
    billing: {
      added: 'Overhead category "{{name}}" added',
      addFailed: "Couldn't add category",
      renamed: 'Renamed to "{{name}}"',
      renameFailed: "Rename failed",
      removed: '"{{name}}" removed',
      deleteFailed: "Delete failed",
      renamePrompt: "New name for this overhead category",
      deleteConfirm:
        'Delete the "{{name}}" overhead category? Any time entries bound to it move under Misc.',
      overheads: "Overhead categories",
      overheadsHint:
        "Project-level buckets for time that doesn't land on a work item — meetings, planning, on-call, etc. `Misc` is the default fallback and can't be removed.",
      default: "default",
      rename: "Rename",
      delete: "Delete",
      newName: "New overhead name…",
      modeSaved: "Billing mode saved",
      modeLabel: "Default billing mode for new work items",
      modeHours: "Bill by hours worked",
      modeFixed: "Fixed delivery price",
      modeHint:
        "New work items start in this mode. The project manager can switch an individual work item and set its fixed price on the work-item page.",
      title: "Billing",
      description:
        "How this project is billed — default mode for new work items, overhead buckets, and the customer rate card.",
      rates: "Billing rates",
      ratesHint:
        'What we charge the customer per hour on this project. Effective-dated — a price change is a new row with a later "Valid from", not an edit. Visible to admin and accountant only.',
    },
    team: {
      title: "Team",
      description:
        "Every new work item is managed by whoever creates it; the rest of its team comes from the defaults below and can be changed on the ticket.",
      saved: "Team saved",
      couldntSave: "Couldn't save the team",
      nobody: "Nobody",
      pickPerson: "Pick a person…",
      defaultTeam: "Default team",
      defaultTeamHint:
        "Who a new work item gets in each slot. Only members with the matching role are offered.",
      developer: "Developer",
      tester: "Tester",
      maintainer: "Maintainer",
      humanTest: "A person tests before production",
      prodTester: "Production tester",
      humanMerge: "A person merges into production",
      prodMaintainer: "Production maintainer",
      prodHint:
        "The production tester runs the last internal test before production and the test on production; the production maintainer merges into production. Earlier steps stay with the regular tester and maintainer.",
      notLive: "Production isn't live yet",
      notLiveHint:
        "For a project built straight on main before it holds real data: Prokop may test and sign in on production. Turn it off before real data arrive.",
      save: "Save team",
      addFailed: "Couldn't add manager",
      removeFailed: "Couldn't remove manager",
      managers: "Managers",
      managersHint:
        "Managers have full access to the project, including its Seafile folder and Vault collections, and can move work items past workflow gates. A GitLab role never makes anyone a manager; managers add each other here.",
      noManagers: "No managers assigned yet.",
      addManager: "Add manager",
      selectUser: "Select a user…",
    },
    merge: {
      and: " and ",
      members_one: "{{count}} member",
      members_few: "{{count}} members",
      members_other: "{{count}} members",
      upgraded: "{{count}} raised to a wider role",
      teamNote: ", along with {{parts}}",
      caughtWorkItems_one: "{{count}} work item whose GitLab issue had already been moved by hand",
      caughtWorkItems_few: "{{count}} work items whose GitLab issue had already been moved by hand",
      caughtWorkItems_other:
        "{{count}} work items whose GitLab issue had already been moved by hand",
      caughtEntries_one: "{{count}} time entry left behind by an earlier manual move",
      caughtEntries_few: "{{count}} time entries left behind by an earlier manual move",
      caughtEntries_other: "{{count}} time entries left behind by an earlier manual move",
      caughtReconciled_one: "{{count}} already where it belongs",
      caughtReconciled_few: "{{count}} already where they belong",
      caughtReconciled_other: "{{count}} already where they belong",
      caughtUp: " Hub also caught up on {{parts}}.",
      nowEmpty: " This project is now empty — delete it below if you no longer need it.",
      leftover_one:
        " {{count}} work item stays here as a closed record of an issue that already lives in the destination.",
      leftover_few:
        " {{count}} work items stay here as closed records of issues that already live in the destination.",
      leftover_other:
        " {{count}} work items stay here as closed records of issues that already live in the destination.",
      overheadCategories_one: " ({{count}} overhead category recreated there)",
      overheadCategories_few: " ({{count}} overhead categories recreated there)",
      overheadCategories_other: " ({{count}} overhead categories recreated there)",
      overheadMoved_one: " Its {{count}} overhead time entry moved along{{categories}}.",
      overheadMoved_few: " Its {{count}} overhead time entries moved along{{categories}}.",
      overheadMoved_other: " Its {{count}} overhead time entries moved along{{categories}}.",
      repoWaiting_one: ", which placed {{count}} waiting time proposal",
      repoWaiting_few: ", which placed {{count}} waiting time proposals",
      repoWaiting_other: ", which placed {{count}} waiting time proposals",
      repoFeeds: " Its repository now feeds the destination too{{waiting}}.",
      destinationFallback: "the destination project",
      refused: "GitLab refused the merge",
      partial: "Merged {{moved}} work items — {{failed}} couldn't move",
      stillHere: "They're still in this project; the reasons are listed below.",
      merged_one: "Merged {{count}} work item into {{target}}",
      merged_few: "Merged {{count}} work items into {{target}}",
      merged_other: "Merged {{count}} work items into {{target}}",
      membersFailed:
        "The work moved, but some members couldn't be given access to the destination — see below.",
      timeFailed:
        "The work moved, but not all of the project's own time landed cleanly — see below.",
      caughtUpWith: "Caught up with {{target}}",
      followed: "Time entries followed{{team}}.",
      failed: "Merge failed.",
      couldntMerge: "Couldn't merge the project",
      title: "Merge into another project",
      description:
        "Move every work item of <b>{{name}}</b> into another project — the GitLab issues move, and each work item's time entries follow. Time logged against the project itself — meetings, planning and the rest — goes too, under the same overhead category on the other side. This project's members are added to the destination so the team keeps access to the work; nobody already there is demoted. Releases and categories are cleared on the way (they belong to this project). This project stays behind, emptied of work items; delete it in the Danger Zone if you want it gone.",
      destination: "Destination project",
      noEligible: "No eligible projects",
      pick: "— pick a project —",
      noMatching: "No matching projects",
      onlyBound: "Only projects bound to GitLab can receive the moved issues.",
      open: "Merge project…",
      stayedBehind: "These work items stayed behind",
      retryHint:
        "Fix the cause and run the merge again — whatever already moved is gone from this project, so a re-run only retries what's left.",
      confirmTitle: "Merge this project?",
      chosenFallback: "the chosen project",
      confirmBody:
        "Every work item in <b>{{name}}</b> moves to <b>{{target}}</b>. GitLab assigns each issue a new number; time entries follow, and this project's members are added to the destination. Releases and categories are cleared. This can't be undone in bulk — you'd have to move each work item back individually.",
      merging: "Merging…",
      confirm: "Merge project",
    },
    access: {
      title: "Access",
      description:
        "Managers get the whole Seafile folder and both Vault collections. Every project member has the main Vault collection and the Seafile folders shared with the team; the prod collection and further folders only when ticked here. GitLab roles decide nothing. Changes reach Seafile and Vault within a minute or two.",
      sharedWithAll: "Shared with every member: {{list}}",
      nothing: "nothing",
      setInFiles: " · set in the Files tab",
      bindingsSaved: "Bindings saved",
      summary: "Seafile: {{seafile}} · Vault: {{vault}}{{prod}}",
      notBound: "not bound",
      bound: "bound",
      plusProd: " (+ prod)",
      folderBound: "Seafile folder bound",
      folderFailed: "Couldn't create the folder",
      belongsTo: "belongs to {{name}}",
      folderIn: "Seafile folder in {{library}}",
      noFolder: "No such folder",
      folderLabel: "Seafile folder",
      createFolder: "Create a folder named after the project",
      archived: "Archived (folder lives in Archiv)",
      vaultCollection: "Vault collection",
      vaultProdCollection: "Vault prod collection (managers and granted members)",
      saveBindings: "Save bindings",
      noCollection: "No such collection",
      fullAccess: "Full access",
      vaultProd: "Vault prod",
      teamOnly: "Team access only",
      manager: "Manager",
      noSeafileHint: "Shares wait until they first sign in to Seafile",
      noSeafile: "no Seafile account yet",
      close: "Close",
      edit: "Edit",
      vaultProdToggle: "Vault prod collection",
      folders: "Seafile folders",
      pathPlaceholder: "Sub/folder path",
      pathLabel: "Folder path",
    },
    gitlab: {
      changeRepo: "repo → {{value}}",
      changePath: "path → {{value}}",
      changeName: "name → {{value}}",
      changeSlug: "slug → {{value}}",
      upToDate: "GitLab link is up to date",
      repaired: "GitLab link repaired",
      repairFailed: "Couldn't repair the GitLab link",
      relinked: "Project re-linked to the GitLab repository",
      relinkFailed: "Couldn't re-link the project",
      attached: "Repository attached",
      attachedAdopted_one: "{{path}} · {{count}} waiting time proposal moved to this project",
      attachedAdopted_few: "{{path}} · {{count}} waiting time proposals moved to this project",
      attachedAdopted_other: "{{path}} · {{count}} waiting time proposals moved to this project",
      attachFailed: "Couldn't attach the repository",
      promoted: "Linked repository changed",
      promoteFailed: "Couldn't make that repository the linked one",
      detached: "Repository detached",
      detachFailed: "Couldn't detach the repository",
      currentRepoFallback: "its current repository",
      promoteConfirm:
        "Link this project to {{name}} instead of {{current}}?\n\nWork items will come from it on the next GitLab sync, and {{current}} stays attached for its tracked time. Existing work items keep pointing at issues in the old repo until you reconnect them below.",
      loadFailed: "Couldn't load the GitLab repository list",
      description:
        "The GitLab repository this project mirrors — its issues become work items here. If the repo was renamed or moved in GitLab, repair the link — the service-link buttons follow the stored path. Further repositories can be attached below for their tracked time.",
      repoPath: "Repository path",
      notStamped: "— not stamped yet (waiting for the first GitLab sync) —",
      mirroredFrom: "Mirrored from",
      adoptName: "Also set the project name to GitLab's current name",
      adoptSlug: "Also set the URL slug from GitLab's repository path",
      adoptHint:
        "Left unchecked, only the stored repository path and group are refreshed — the Hub name and slug stay as you set them.",
      repairing: "Repairing…",
      repair: "Repair from GitLab",
      repairFailedHint: "Repair failed, or the repo was deleted and recreated?",
      changeLinked: "Change the linked repository",
      relinkIntro:
        "Points this Hub project at a different GitLab repo — its issues, members and milestones take over on the next GitLab sync. Repos already bound to another Hub project don't show up here.",
      loadingRepos: "Loading GitLab repositories…",
      repoLabel: "GitLab repository",
      noUnbound: "No unbound GitLab repos found",
      searchRepos: "Search GitLab repos…",
      noMatching: "No matching repos",
      relinking: "Re-linking…",
      relink: "Re-link project",
      alsoFeeding: "Also feeding this project",
      noOthers: "No other repositories — only the one above.",
      makeLinked: "Make it the linked repo",
      detach: "Detach",
      secondaryHint:
        "Time tracked in these repositories is proposed against this project — and attaching one places the proposals already waiting behind it. Their issues are not read: work items come from the linked repository above only. Attached the repo this project is really about? Make it the linked one — the repository above takes its place here.",
      attachOpen: "Attach a repository",
      attachIntro:
        "Repos already bound to — or already feeding — a Hub project don't show up here.",
      attachLabel: "Repository to attach",
      noUnclaimed: "No unclaimed GitLab repos found",
      attaching: "Attaching…",
      attach: "Attach repository",
      linkedRepoFallback: "the linked repository",
      reconnect: {
        would_one: "{{count}} work item would be reconnected",
        would_few: "{{count}} work items would be reconnected",
        would_other: "{{count}} work items would be reconnected",
        allLive: "Every work item already points at a live GitLab issue",
        done: "Work items reconnected to GitLab",
        nothing: "Nothing to reconnect — every work item was already valid",
        summary: "{{valid}} already valid · {{rematched}} re-matched · {{recreated}} recreated",
        failedSuffix: " · {{failed}} failed",
        failed: "Couldn't reconnect the work items",
        title: "Work items ↔ GitLab issues",
        intro:
          "After a repo is deleted and recreated, the work items still point at issues that no longer exist — they 404 in GitLab and their state can't be written back. This re-points each one at the live issue with the same title, and recreates any issue that's gone for good. Safe to run repeatedly.",
        working: "Working…",
        preview: "Preview reconnect",
        resultWould: "Would reconnect: ",
        resultDone: "Reconnected: ",
        counts_one:
          "{{count}} work item · {{valid}} already valid · {{rematched}} re-matched · {{recreated}} recreated",
        counts_few:
          "{{count}} work items · {{valid}} already valid · {{rematched}} re-matched · {{recreated}} recreated",
        counts_other:
          "{{count}} work items · {{valid}} already valid · {{rematched}} re-matched · {{recreated}} recreated",
        applyNote:
          "Applying this re-points {{rematched}} work items at existing issues and <strong>creates {{recreated}} new issues</strong> in <mono>{{repo}}</mono>.",
        reconnecting: "Reconnecting…",
        apply: "Apply",
      },
    },
  },
};

export const projSetCs: typeof projSetEn = {
  projSet: {
    common: {
      saving: "Ukládání…",
      saveFailed: "Uložení se nezdařilo.",
      tryAgain: "Zkuste to znovu.",
      couldntSave: "Nepodařilo se uložit",
      error: "Chyba",
      add: "Přidat",
      remove: "Odebrat",
      name: "Název",
      save: "Uložit",
      cancel: "Zrušit",
      productionMaintainerRequired:
        "Nejdřív v Týmu vyberte production maintainera: do každého prostředí po prvním merguje člověk.",
    },
    page: {
      noAccess: "Tento projekt může upravovat jen manažer projektu nebo administrátor Hubu.",
      title: "Úprava projektu",
    },
    danger: {
      deleteFailed: "Smazání se nezdařilo.",
      couldntDelete: "Projekt se nepodařilo smazat",
      title: "Nebezpečná zóna",
      description: "Trvale smaže tento projekt i všechna jeho data. Tuto akci nelze vrátit zpět.",
      confirmLabel: "Pro potvrzení napište název projektu:",
      deleting: "Mazání…",
      delete: "Smazat projekt",
    },
    sla: {
      saved: "SLA uloženo",
      couldntSave: "SLA se nepodařilo uložit",
      title: "SLA incidentů",
      description:
        "Maximální doba reakce podle priority. Podle ní se automaticky počítají termíny nových incidentů.",
      urgent: "Urgentní",
      high: "Vysoká",
      normal: "Normální",
      low: "Nízká",
      save: "Uložit SLA",
      hours: "hodin",
      businessDays: "pracovních dnů",
    },
    tester: {
      failed: "Kontrolu se nepodařilo spustit.",
      title: "Přístup Prokopa",
      description:
        "Co Prokop najde, když tento projekt otevře: testovací prohlížeč, svou schránku pro registrace, účet, který pro web drží Vault, a jestli ho tento účet skutečně přihlásí. Nic se nikam nezapisuje — jen to zkusí.",
      checking: "Kontroluji…",
      check: "Zkontrolovat {{env}}",
      noEnv: "Pro testování není zapnuté žádné prostředí (do produkce se nikdy nepřihlašuje).",
    },
    external: {
      saved: "Externí služby uloženy",
      couldntSave: "Externí služby se nepodařilo uložit",
      title: "Externí služby (Prokop)",
      description:
        "Systémy, které může Prokop otevřít v prohlížeči, když se účinek tiketu projeví mimo testovaný web — ERP zákazníka, administrace platební brány, fakturační systém. Účet ke každému z nich najde ve Vaultu podle jeho URL a pak s ním volně pracuje, proto tam <strong>ukládejte vždy jen testovací přihlašovací údaje</strong>. Cokoli, co tu není uvedeno, je pro něj nedostupné.",
      notes: "K čemu slouží",
      folderUid: "Uid složky v Grafaně",
      folderUidHint:
        "Uid složky projektu v Grafaně (část adresy složky hned za /dashboards/f/, např. grafana.netlion.cz/dashboards/f/afp5ev4kc24n4c/hravesporty dá afp5ev4kc24n4c, ne název na konci). Když je vyplněné, berou se pravidla, alerty i dashboardy projektu jen z této složky, i když se jmenuje jinak. Bez uid Hub hledá složku podle názvu projektu (HraveSporty.cz najde HraveSporty), a jen projekt, který nemá ani uid, ani takovou složku, se dozví, že nemá složku přiřazenou. Složka bez pravidel se zobrazí jako prázdná, což je něco jiného než nenakonfigurovaná.",
      add: "Přidat službu",
      save: "Uložit externí služby",
    },
    freelo: {
      title: "Freelo",
      description:
        "Obousměrná synchronizace úkolů projektu s todo listem ve Freelu. Hodiny se ve Freelu zapisují pod uživatelem, jehož e-mail a API klíč tu zadáte. Klíč se ukládá zašifrovaně a znovu se nezobrazí. Synchronizace běží každých 5 minut; když Freelo nejede nebo odmítne klíč, zastaví se a poslední chyba je vidět tady.",
      projectId: "Id projektu ve Freelu",
      todolistId: "Id todo listu",
      email: "E-mail uživatele Freela",
      apiKey: "API klíč",
      apiKeySet: "Nastaven (prázdné pole ho ponechá)",
      apiKeyMissing: "Nenastaven",
      enabled: "Synchronizace zapnuta",
      lastError: "Poslední chyba",
      lastSuccess: "Poslední úspěšný běh",
      never: "nikdy",
      saved: "Freelo uloženo",
      couldntSave: "Napojení na Freelo se nepodařilo uložit",
      save: "Uložit Freelo",
    },
    general: {
      colour: "Barva",
      colourLabel: "Barva projektu",
      colourHexLabel: "Barva projektu (hex)",
      automatic: "Automaticky",
      colourHint:
        "Zobrazuje se u názvu projektu v celém Hubu. Automaticky se odvodí z projektu — aktuálně {{colour}}.",
      colours: {
        blue: "modrá",
        orange: "oranžová",
        aqua: "azurová",
        yellow: "žlutá",
        magenta: "purpurová",
        green: "zelená",
        violet: "fialová",
        red: "červená",
      },
      saved: "Projekt uložen",
      saveFailed: "Uložení se nezdařilo — zkontrolujte pole a svá oprávnění.",
      title: "Obecné",
      description: "Základní informace o projektu",
      slug: "Slug",
      slugHint: "URL slug tohoto projektu.",
      customer: "Zákazník",
      descriptionLabel: "Popis",
      descriptionPlaceholder: "Popis projektu",
      mirroredFrom: "Zrcadleno z",
      mirroredHint:
        "Upstream, se kterým se repozitář v GitLabu obousměrně zrcadlí. Je-li tu URL na GitHub, merge brány Hubu odmítnou mergovat, dokud je GitHub před GitLabem (viz wiki → Synchronizace).",
      label: "Popisek",
      public: "Veřejný projekt",
      save: "Uložit změny",
    },
    workflow: {
      saved: "Workflow uloženo",
      couldntSave: "Workflow se nepodařilo uložit",
      title: "Workflow",
      description: "Zapněte nebo vypněte funkce Hubu pro tento projekt a upravte vývojový cyklus.",
      features: "Funkce",
      releases: "Vydání",
      releasesDesc:
        "Seskupujte tikety do ručně spravovaných vydání (zrcadlí se jako milníky v GitLabu).",
      sla: "SLA incidentů",
      slaDesc: "Termíny incidentů se odvodí z cílů SLA projektu.",
      spec: "Technická specifikace",
      specDesc:
        "Přidá fázi sepsání specifikace a schválení klientem mezi Nový a odhad. Vlastní ji manažer.",
      fallback: "Agenti jen v době volna",
      fallbackDesc:
        "Metoděj a Prokop berou tikety tohoto projektu, jen když nemají nic na jiných projektech.",
      priority: "Priorita projektu",
      priorityDesc:
        "Agenti dokončí projekty s vyšší prioritou dřív, než začnou nové tickety méně důležitých.",
      priority_critical: "Kritická",
      priority_high: "Vysoká",
      priority_normal: "Normální",
      priority_low: "Nízká",
      lifecycle: "Kroky životního cyklu",
      lifecycleHint:
        "Volitelné kroky ve workflow funkcí. Které testovací kroky existují, se nastavuje pro každé prostředí v kartě Prostředí.",
      estimation: "Odhad",
      estimationDesc: "Vývojář práci odhadne a manažer odhad před zahájením schválí.",
      save: "Uložit workflow",
    },
    env: {
      saved: "Prostředí uložena",
      couldntSave: "Prostředí se nepodařilo uložit",
      title: "Prostředí",
      description:
        "Každé prostředí zapněte nebo vypněte, nastavte jeho URL a určete, jaké testovací kroky na něm probíhají — každý zaškrtnutý krok přidá do workflow funkcí jeden stav (interní testování vždy předchází testování klientem).",
      enabledLabel: "{{env}} zapnuto",
      internalLabel: "{{env}} interní testování",
      clientLabel: "{{env}} testování klientem",
      weTest: "Testujeme tady",
      clientTests: "Klient testuje tady",
      personOnly:
        "Na produkci testuje jen člověk: chcete-li tu testovat sami, zapněte v kartě Tým volbu „Před produkcí testuje člověk“.",
      save: "Uložit prostředí",
      restarting: "Restartuje se {{name}}",
      pipelineLink: "Pipeline v GitLabu",
      restartFailed: "Prostředí {{name}} se nepodařilo restartovat",
      restartHint: "Spustí novou pipeline na větvi, která stack znovu nasadí",
      startingPipeline: "Spouštím pipeline…",
      restart: "Restartovat {{name}}",
      idleNote:
        "Dev nebo test prostředí, ve kterém 20 dní neběžela žádná nová pipeline, se na serveru zastaví. Restart ho vrátí zpět i s daty.",
    },
    billing: {
      added: "Režijní kategorie „{{name}}“ přidána",
      addFailed: "Kategorii se nepodařilo přidat",
      renamed: "Přejmenováno na „{{name}}“",
      renameFailed: "Přejmenování se nezdařilo",
      removed: "„{{name}}“ odebrána",
      deleteFailed: "Smazání se nezdařilo",
      renamePrompt: "Nový název této režijní kategorie",
      deleteConfirm:
        "Smazat režijní kategorii „{{name}}“? Časové záznamy, které k ní patří, se přesunou do kategorie Misc.",
      overheads: "Režijní kategorie",
      overheadsHint:
        "Projektové přihrádky pro čas, který nepatří k žádnému tiketu — schůzky, plánování, pohotovost apod. `Misc` je výchozí záložní kategorie a nelze ji odebrat.",
      default: "výchozí",
      rename: "Přejmenovat",
      delete: "Smazat",
      newName: "Název nové režijní kategorie…",
      modeSaved: "Režim fakturace uložen",
      modeLabel: "Výchozí režim fakturace nových tiketů",
      modeHours: "Fakturovat odpracované hodiny",
      modeFixed: "Pevná cena za dodání",
      modeHint:
        "Nové tikety začínají v tomto režimu. Manažer projektu může režim u konkrétního tiketu přepnout a pevnou cenu nastavit na stránce tiketu.",
      title: "Fakturace",
      description:
        "Jak se tento projekt fakturuje — výchozí režim nových tiketů, režijní kategorie a ceník pro zákazníka.",
      rates: "Fakturační sazby",
      ratesHint:
        "Kolik zákazníkovi na tomto projektu účtujeme za hodinu. Sazby mají platnost od data — změna ceny je nový řádek s pozdějším „Platí od“, ne úprava. Vidí je jen administrátor a účetní.",
    },
    team: {
      title: "Tým",
      description:
        "Každý nový tiket spravuje ten, kdo ho vytvoří; zbytek týmu se vezme z výchozích hodnot níže a na tiketu ho lze změnit.",
      saved: "Tým uložen",
      couldntSave: "Tým se nepodařilo uložit",
      nobody: "Nikdo",
      pickPerson: "Vyberte osobu…",
      defaultTeam: "Výchozí tým",
      defaultTeamHint:
        "Koho nový tiket dostane na jednotlivé role. Nabízejí se jen členové s odpovídající rolí.",
      developer: "Vývojář",
      tester: "Tester",
      maintainer: "Maintainer",
      humanTest: "Před produkcí testuje člověk",
      prodTester: "Produkční tester",
      humanMerge: "Do produkce merguje člověk",
      prodMaintainer: "Produkční maintainer",
      prodHint:
        "Produkční tester provádí poslední interní test před produkcí a test na produkci; produkční maintainer merguje do produkce. Dřívější kroky zůstávají běžnému testerovi a maintainerovi.",
      notLive: "Produkce ještě neběží ostře",
      notLiveHint:
        "Pro projekt stavěný rovnou na main, než obsahuje skutečná data: Prokop smí na produkci testovat a přihlašovat se. Vypněte to dřív, než přijdou skutečná data.",
      save: "Uložit tým",
      addFailed: "Manažera se nepodařilo přidat",
      removeFailed: "Manažera se nepodařilo odebrat",
      managers: "Manažeři",
      managersHint:
        "Manažeři mají plný přístup k projektu, včetně jeho složky v Seafile a kolekcí ve Vaultu, a mohou posouvat tikety přes brány workflow. Role v GitLabu nikoho manažerem neudělá; manažeři se tu přidávají navzájem.",
      noManagers: "Zatím nejsou přiřazeni žádní manažeři.",
      addManager: "Přidat manažera",
      selectUser: "Vyberte uživatele…",
    },
    merge: {
      and: " a ",
      members_one: "{{count}} člen",
      members_few: "{{count}} členové",
      members_other: "{{count}} členů",
      upgraded: "{{count}} s rozšířenou rolí",
      teamNote: ", včetně {{parts}}",
      caughtWorkItems_one: "{{count}} tiket, jehož issue v GitLabu už někdo přesunul ručně",
      caughtWorkItems_few: "{{count}} tikety, jejichž issue v GitLabu už někdo přesunul ručně",
      caughtWorkItems_other: "{{count}} tiketů, jejichž issue v GitLabu už někdo přesunul ručně",
      caughtEntries_one: "{{count}} časový záznam zbylý po dřívějším ručním přesunu",
      caughtEntries_few: "{{count}} časové záznamy zbylé po dřívějším ručním přesunu",
      caughtEntries_other: "{{count}} časových záznamů zbylých po dřívějším ručním přesunu",
      caughtReconciled_one: "{{count}} už je tam, kam patří",
      caughtReconciled_few: "{{count}} už jsou tam, kam patří",
      caughtReconciled_other: "{{count}} už je tam, kam patří",
      caughtUp: " Hub navíc dorovnal: {{parts}}.",
      nowEmpty: " Tento projekt je teď prázdný — pokud ho už nepotřebujete, smažte ho níže.",
      leftover_one:
        " {{count}} tiket tu zůstává jako uzavřený záznam issue, které už je v cílovém projektu.",
      leftover_few:
        " {{count}} tikety tu zůstávají jako uzavřené záznamy issue, která už jsou v cílovém projektu.",
      leftover_other:
        " {{count}} tiketů tu zůstává jako uzavřené záznamy issue, která už jsou v cílovém projektu.",
      overheadCategories_one: " ({{count}} režijní kategorie tam byla znovu vytvořena)",
      overheadCategories_few: " ({{count}} režijní kategorie tam byly znovu vytvořeny)",
      overheadCategories_other: " ({{count}} režijních kategorií tam bylo znovu vytvořeno)",
      overheadMoved_one:
        " Jeho {{count}} režijní časový záznam se přesunul spolu s ním{{categories}}.",
      overheadMoved_few:
        " Jeho {{count}} režijní časové záznamy se přesunuly spolu s ním{{categories}}.",
      overheadMoved_other:
        " Jeho {{count}} režijních časových záznamů se přesunulo spolu s ním{{categories}}.",
      repoWaiting_one: ", čímž se zařadil {{count}} čekající návrh času",
      repoWaiting_few: ", čímž se zařadily {{count}} čekající návrhy času",
      repoWaiting_other: ", čímž se zařadilo {{count}} čekajících návrhů času",
      repoFeeds: " Jeho repozitář teď plní i cílový projekt{{waiting}}.",
      destinationFallback: "cílový projekt",
      refused: "GitLab sloučení odmítl",
      partial: "Sloučeno tiketů: {{moved}} — {{failed}} se nepodařilo přesunout",
      stillHere: "Zůstávají v tomto projektu; důvody jsou vypsané níže.",
      merged_one: "Sloučen {{count}} tiket do projektu {{target}}",
      merged_few: "Sloučeny {{count}} tikety do projektu {{target}}",
      merged_other: "Sloučeno {{count}} tiketů do projektu {{target}}",
      membersFailed:
        "Práce se přesunula, ale některým členům se nepodařilo dát přístup do cílového projektu — viz níže.",
      timeFailed:
        "Práce se přesunula, ale ne všechen vlastní čas projektu dorazil bez problémů — viz níže.",
      caughtUpWith: "Dorovnáno s projektem {{target}}",
      followed: "Časové záznamy se přesunuly{{team}}.",
      failed: "Sloučení se nezdařilo.",
      couldntMerge: "Projekt se nepodařilo sloučit",
      title: "Sloučit do jiného projektu",
      description:
        "Přesune všechny tikety projektu <b>{{name}}</b> do jiného projektu — issue v GitLabu se přesunou a časové záznamy každého tiketu je následují. Čas zapsaný přímo na projekt — schůzky, plánování a další — jde také, pod stejnou režijní kategorií na druhé straně. Členové tohoto projektu se přidají do cílového, aby tým o práci nepřišel; nikdo, kdo tam už je, není degradován. Vydání a kategorie se cestou smažou (patří tomuto projektu). Tento projekt zůstane prázdný, bez tiketů; chcete-li ho odstranit, smažte ho v Nebezpečné zóně.",
      destination: "Cílový projekt",
      noEligible: "Žádné vhodné projekty",
      pick: "— vyberte projekt —",
      noMatching: "Žádné odpovídající projekty",
      onlyBound: "Přesunutá issue mohou přijmout jen projekty napojené na GitLab.",
      open: "Sloučit projekt…",
      stayedBehind: "Tyto tikety zůstaly",
      retryHint:
        "Odstraňte příčinu a spusťte sloučení znovu — co se už přesunulo, v tomto projektu není, takže opakování zkusí jen zbytek.",
      confirmTitle: "Sloučit tento projekt?",
      chosenFallback: "vybraný projekt",
      confirmBody:
        "Každý tiket v projektu <b>{{name}}</b> se přesune do projektu <b>{{target}}</b>. GitLab přidělí každému issue nové číslo; časové záznamy je následují a členové tohoto projektu se přidají do cílového. Vydání a kategorie se smažou. Hromadně to nejde vrátit — každý tiket byste museli přesouvat zpět zvlášť.",
      merging: "Slučuji…",
      confirm: "Sloučit projekt",
    },
    access: {
      title: "Přístup",
      description:
        "Manažeři dostanou celou složku v Seafile a obě kolekce ve Vaultu. Každý člen projektu má hlavní kolekci ve Vaultu a složky v Seafile sdílené s týmem; produkční kolekci a další složky jen tehdy, když je tu zaškrtnete. Role v GitLabu nerozhodují o ničem. Změny se do Seafile a Vaultu promítnou do minuty či dvou.",
      sharedWithAll: "Sdíleno se všemi členy: {{list}}",
      nothing: "nic",
      setInFiles: " · nastavuje se na záložce Soubory",
      bindingsSaved: "Vazby uloženy",
      summary: "Seafile: {{seafile}} · Vault: {{vault}}{{prod}}",
      notBound: "nepřipojeno",
      bound: "připojeno",
      plusProd: " (+ prod)",
      folderBound: "Složka v Seafile připojena",
      folderFailed: "Složku se nepodařilo vytvořit",
      belongsTo: "patří projektu {{name}}",
      folderIn: "Složka v Seafile v knihovně {{library}}",
      noFolder: "Taková složka neexistuje",
      folderLabel: "Složka v Seafile",
      createFolder: "Vytvořit složku podle názvu projektu",
      archived: "Archivováno (složka je v Archivu)",
      vaultCollection: "Kolekce ve Vaultu",
      vaultProdCollection: "Produkční kolekce ve Vaultu (manažeři a členové s přístupem)",
      saveBindings: "Uložit vazby",
      noCollection: "Taková kolekce neexistuje",
      fullAccess: "Plný přístup",
      vaultProd: "Vault prod",
      teamOnly: "Jen přístup týmu",
      manager: "Manažer",
      noSeafileHint: "Sdílení čeká, dokud se poprvé nepřihlásí do Seafile",
      noSeafile: "zatím bez účtu v Seafile",
      close: "Zavřít",
      edit: "Upravit",
      vaultProdToggle: "Produkční kolekce ve Vaultu",
      folders: "Složky v Seafile",
      pathPlaceholder: "Podsložka/cesta",
      pathLabel: "Cesta ke složce",
    },
    gitlab: {
      changeRepo: "repozitář → {{value}}",
      changePath: "cesta → {{value}}",
      changeName: "název → {{value}}",
      changeSlug: "slug → {{value}}",
      upToDate: "Propojení s GitLabem je aktuální",
      repaired: "Propojení s GitLabem opraveno",
      repairFailed: "Propojení s GitLabem se nepodařilo opravit",
      relinked: "Projekt znovu propojen s repozitářem v GitLabu",
      relinkFailed: "Projekt se nepodařilo znovu propojit",
      attached: "Repozitář připojen",
      attachedAdopted_one: "{{path}} · {{count}} čekající návrh času přesunut do tohoto projektu",
      attachedAdopted_few: "{{path}} · {{count}} čekající návrhy času přesunuty do tohoto projektu",
      attachedAdopted_other:
        "{{path}} · {{count}} čekajících návrhů času přesunuto do tohoto projektu",
      attachFailed: "Repozitář se nepodařilo připojit",
      promoted: "Propojený repozitář změněn",
      promoteFailed: "Tento repozitář se nepodařilo nastavit jako propojený",
      detached: "Repozitář odpojen",
      detachFailed: "Repozitář se nepodařilo odpojit",
      currentRepoFallback: "jeho aktuální repozitář",
      promoteConfirm:
        "Propojit tento projekt s repozitářem {{name}} místo {{current}}?\n\nTikety budou pocházet z něj při příští synchronizaci s GitLabem a {{current}} zůstane připojený kvůli svému sledovanému času. Stávající tikety budou dál odkazovat na issue ve starém repozitáři, dokud je níže znovu nepropojíte.",
      loadFailed: "Seznam repozitářů z GitLabu se nepodařilo načíst",
      description:
        "Repozitář v GitLabu, který tento projekt zrcadlí — jeho issue se tu stávají tikety. Pokud byl repozitář v GitLabu přejmenován nebo přesunut, opravte propojení — tlačítka odkazů na služby se řídí uloženou cestou. Další repozitáře lze níže připojit kvůli jejich sledovanému času.",
      repoPath: "Cesta k repozitáři",
      notStamped: "— zatím nezapsáno (čeká se na první synchronizaci s GitLabem) —",
      mirroredFrom: "Zrcadleno z",
      adoptName: "Nastavit také název projektu podle aktuálního názvu v GitLabu",
      adoptSlug: "Nastavit také URL slug podle cesty repozitáře v GitLabu",
      adoptHint:
        "Když nic nezaškrtnete, obnoví se jen uložená cesta k repozitáři a skupina — název a slug v Hubu zůstanou, jak jste je nastavili.",
      repairing: "Opravuji…",
      repair: "Opravit z GitLabu",
      repairFailedHint: "Oprava selhala, nebo byl repozitář smazán a vytvořen znovu?",
      changeLinked: "Změnit propojený repozitář",
      relinkIntro:
        "Namíří tento projekt v Hubu na jiný repozitář v GitLabu — jeho issue, členové a milníky se převezmou při příští synchronizaci s GitLabem. Repozitáře, které už patří jinému projektu v Hubu, se tu nezobrazí.",
      loadingRepos: "Načítám repozitáře z GitLabu…",
      repoLabel: "Repozitář v GitLabu",
      noUnbound: "Nenalezeny žádné nepropojené repozitáře v GitLabu",
      searchRepos: "Hledat repozitáře v GitLabu…",
      noMatching: "Žádné odpovídající repozitáře",
      relinking: "Propojuji…",
      relink: "Znovu propojit projekt",
      alsoFeeding: "Další zdroje tohoto projektu",
      noOthers: "Žádné další repozitáře — jen ten výše.",
      makeLinked: "Nastavit jako propojený",
      detach: "Odpojit",
      secondaryHint:
        "Čas zapsaný v těchto repozitářích se navrhuje k tomuto projektu — a připojením repozitáře se zařadí návrhy, které na něj už čekaly. Jejich issue se nečtou: tikety pocházejí jen z propojeného repozitáře výše. Připojili jste repozitář, o kterém tento projekt skutečně je? Nastavte ho jako propojený — repozitář výše tu zaujme jeho místo.",
      attachOpen: "Připojit repozitář",
      attachIntro: "Repozitáře, které už patří projektu v Hubu nebo ho už plní, se tu nezobrazí.",
      attachLabel: "Repozitář k připojení",
      noUnclaimed: "Nenalezeny žádné volné repozitáře v GitLabu",
      attaching: "Připojuji…",
      attach: "Připojit repozitář",
      linkedRepoFallback: "propojený repozitář",
      reconnect: {
        would_one: "{{count}} tiket by se znovu propojil",
        would_few: "{{count}} tikety by se znovu propojily",
        would_other: "{{count}} tiketů by se znovu propojilo",
        allLive: "Každý tiket už odkazuje na existující issue v GitLabu",
        done: "Tikety znovu propojeny s GitLabem",
        nothing: "Není co propojovat — každý tiket už byl v pořádku",
        summary:
          "{{valid}} už v pořádku · {{rematched}} znovu přiřazeno · {{recreated}} znovu vytvořeno",
        failedSuffix: " · {{failed}} selhalo",
        failed: "Tikety se nepodařilo znovu propojit",
        title: "Tikety ↔ issue v GitLabu",
        intro:
          "Po smazání a opětovném vytvoření repozitáře tikety stále odkazují na issue, která už neexistují — v GitLabu hlásí 404 a jejich stav se nedá zapsat zpět. Tohle každý z nich přepojí na existující issue se stejným názvem a issue, která zmizela, vytvoří znovu. Lze spouštět opakovaně.",
        working: "Pracuji…",
        preview: "Náhled propojení",
        resultWould: "Znovu by se propojilo: ",
        resultDone: "Znovu propojeno: ",
        counts_one:
          "{{count}} tiket · {{valid}} už v pořádku · {{rematched}} znovu přiřazeno · {{recreated}} znovu vytvořeno",
        counts_few:
          "{{count}} tikety · {{valid}} už v pořádku · {{rematched}} znovu přiřazeno · {{recreated}} znovu vytvořeno",
        counts_other:
          "{{count}} tiketů · {{valid}} už v pořádku · {{rematched}} znovu přiřazeno · {{recreated}} znovu vytvořeno",
        applyNote:
          "Použitím se {{rematched}} tiketů přepojí na existující issue a <strong>vytvoří se {{recreated}} nových issue</strong> v <mono>{{repo}}</mono>.",
        reconnecting: "Propojuji…",
        apply: "Použít",
      },
    },
  },
};
